import type { Team } from '@domain/schemas/team.schema';
import { UserRole } from '@domain/schemas/user-role';
import { tenantConfig } from '@root/config/tenants';
import { get } from 'lodash-es';

import { isNonNullish } from '../isNonNullish';

// Only the fields these helpers read, so the SPA's signed-in user and the domain User both fit.
type UserWithRole = { role?: UserRole; attributes?: Record<string, unknown> };
type UserWithTeams = { tenantIds?: number[] };

export const getUserRole = (user: UserWithRole | null): UserRole | undefined =>
  get(user, 'attributes["custom:role"]') ?? (get(user, 'role') as UserRole);
const TEAMS = tenantConfig.teams ?? [];

export const getTeamNamesByTeamIds = (teamIds: number[]): string[] =>
  teamIds.map((teamId: number) => TEAMS?.find((team) => team.id === teamId)?.name ?? '').filter((i: string) => i);

export const getUserTeamNames = (user: UserWithTeams | null, showAllIfUserNotAssignedToAny = false): string[] => {
  const tenantIds = user?.tenantIds ?? [];
  const userTeams = getTeamNamesByTeamIds(tenantIds);

  if (showAllIfUserNotAssignedToAny && userTeams.length === 0) {
    return TEAMS.map((team) => team.name);
  }

  return userTeams;
};

export const getUserTeams = (user: UserWithTeams | null, showAllIfUserNotAssignedToAny = false): Team[] => {
  const tenantIds = user?.tenantIds ?? [];
  const userTeams = tenantIds.map((teamId: number) => TEAMS?.find((team) => team.id === teamId)).filter(isNonNullish);

  if (showAllIfUserNotAssignedToAny && userTeams.length === 0) {
    return TEAMS;
  }

  return userTeams;
};

export const getAllTeams = (): Team[] => TEAMS;

export const ADMIN_ROLES = [UserRole.Admin, UserRole.Superadmin, UserRole.TemplateManager];

/**
 *
 * @param user - a user with a role
 * @param matchStrict - if true, only UserRole.Admin will be considered as an admin
 */
export const isAdmin = (user: UserWithRole | null, matchStrict: boolean = false) => {
  const role = getUserRole(user);

  if (!role) {
    return false;
  }

  if (matchStrict) {
    return role === UserRole.Admin;
  }

  return ADMIN_ROLES.includes(role);
};

export const isSuperadmin = (user: UserWithRole | null) => {
  const role = getUserRole(user);
  return role === UserRole.Superadmin;
};

export const isTemplateManager = (user: UserWithRole | null) => {
  const role = getUserRole(user);
  return role === UserRole.TemplateManager;
};
